<template>
  <div class="app-multi-range">
    <div class="app-multi-range__fields row">
      <div class="col-6">
        <div class="d-flex justify-content-start align-items-center">
          <small class="me-2">Від</small>
          <app-input
            type="number"
            v-model="internalFrom"
            :min="min"
            :max="max"
            :step="step"
            class="app-multi-range__input w-100"
            @focus="$emit('focus', $event)"
            @blur="$emit('blur', $event)"
          />
        </div>
      </div>
      <div class="col-6">
        <div class="d-flex align-items-center">
          <small class="me-2">До</small>
          <app-input
            type="number"
            v-model="internalTo"
            :min="min"
            :max="max"
            :step="step"
            class="app-multi-range__input w-100"
            @focus="$emit('focus', $event)"
            @blur="$emit('blur', $event)"
          />
        </div>
      </div>
    </div>

    <div class="app-multi-range__sliders">
      <div class="app-multi-range__track-fill" :style="trackStyle"></div>
      <input
        class="app-multi-range__slider"
        type="range"
        :min="min"
        :max="max"
        :step="step"
        v-model="internalFrom"
      />
      <input
        class="app-multi-range__slider"
        type="range"
        :min="min"
        :max="max"
        :step="step"
        v-model="internalTo"
      />
    </div>
  </div>
</template>

<script>
import { computed, watch } from "vue";
import AppInput from "@/components/common/ui/AppInput.vue";

export default {
  name: "AppMultiRange",
  components: {
    AppInput,
  },
  props: {
    from: {
      type: Number,
      required: true,
    },
    to: {
      type: Number,
      required: true,
    },
    min: {
      type: Number,
      default: 0,
    },
    max: {
      type: Number,
      default: 10000,
    },
    step: {
      type: Number,
      default: 1,
    },
  },
  emits: ["update:from", "update:to", "blur", "focus"],
  setup(props, { emit }) {
    const internalFrom = computed({
      get: () => props.from,
      set: (val) => emit("update:from", Number(val)),
    });

    const internalTo = computed({
      get: () => props.to,
      set: (val) => emit("update:to", Number(val)),
    });

    watch(internalFrom, () => {
      switch (true) {
        case internalFrom.value > internalTo.value - props.step:
          internalFrom.value = internalTo.value - props.step;
          break;
        case internalFrom.value < props.min:
          internalFrom.value = props.min;
          break;
      }
    });

    watch(internalTo, () => {
      switch (true) {
        case internalTo.value < internalFrom.value + props.step:
          internalTo.value = internalFrom.value + props.step;
          break;
        case internalTo.value > props.max:
          internalTo.value = props.max;
          break;
      }
    });

    const trackStyle = computed(() => {
      const range = props.max - props.min;
      const left = ((props.from - props.min) / range) * 100;
      const right = ((props.to - props.min) / range) * 100;
      return {
        left: `${left}%`,
        width: `${right - left}%`,
      };
    });

    return {
      internalFrom,
      internalTo,
      trackStyle,
    };
  },
};
</script>

<style scoped lang="less">
@import "@/styles/_variables.less";

.app-multi-range {
  &__fields {
    margin-bottom: 25px;
  }

  &__input {
    margin: 0 !important;

    ::v-deep(.form-control) {
      border: 1px solid @white;
      border-radius: 5px;
      padding: 6px 10px;
      font-style: normal;
      font-weight: 400;
      font-size: 15px;
      line-height: 18px;
      color: @white;
    }
  }

  &__sliders {
    position: relative;
    height: 24px;
  }

  &__track-fill {
    position: absolute;
    top: 0;
    height: 6px;
    background-color: @yellow;
    border-radius: 4px;
    z-index: 1;
  }

  &__slider {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    appearance: none;
    height: 6px;
    border-radius: 6px;
    background: #dadada;
    pointer-events: all;

    &::-webkit-slider-thumb {
      appearance: none;
      width: 24px;
      height: 24px;
      background: url("@/assets/svg/range-thumb.svg") no-repeat center;
      background-size: contain;
      cursor: pointer;
      position: relative;
      z-index: 2;
    }

    &::-moz-range-thumb {
      width: 24px;
      height: 24px;
      background: url("@/assets/svg/range-thumb.svg") no-repeat center;
      background-size: contain;
      cursor: pointer;
      position: relative;
      z-index: 2;
    }
  }
}
</style>
